import * as React from 'react';
import { useEffect, useState } from 'react';
import { FormField, Select, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { LfPrincipalTypeSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/lfPrincipalTypeSelector';
import { generateIdFromIdInfo, IdInfo } from 'src/components/utils/arnUtil';
import { DATASET_RESOURCE_TYPE, RESOURCE_ITEM } from 'src/commons/constants';
import { ResourceSummaryTable } from 'src/components/catalog/datasetdetail/accessSelectModal/resourceSummaryTable';
import { createDatasetDetailLink } from 'src/routes';
import { PermissionDropdownOption } from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { GalaxiGroupDropdown } from 'src/components/catalog/datasetdetail/accessSelectModal/galaxiGroupDropdown';
import { AccountAssociationCheckboxes } from 'src/components/catalog/datasetdetail/accessSelectModal/accountAssociationCheckboxes';
import { isValidWorkspace } from 'src/commons/validationUtils';
import { getFoundryRoleARN } from 'src/commons/common';
import { CartAccessItem } from 'src/components/common/checkout/common';

export interface ResourceLevelAccessSelectorProps {
  idInfo: IdInfo;
  activeGroup: string;
  activeWorkspace: any;
  loading: boolean;
  options: any;
  setCartItemsToAdd: any;
  isDWaaSWorkspace?: boolean;
  galaxiGroupOptions?: any;
}

export const ResourceLevelAccessSelector = (props: ResourceLevelAccessSelectorProps) => {
  const [selectedPermission, setSelectedPermission] = useState<PermissionDropdownOption>(undefined);
  const [resourceSummaryItems, setResourceSummaryItems] = useState<any[]>([]);
  const [principal, setPrincipal] = useState<string>();
  const [selectedGalaxiGroup, setSelectedGalaxiGroup] = useState(undefined);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const flattenDatasetAccess = (selectedPermission, principal: string): CartAccessItem => {
    return {
      datasetName: selectedPermission.datasetName,
      databaseName: selectedPermission.databaseName,
      tableName: selectedPermission.datasetName,
      catalogId: selectedPermission.catalogId,
      region: props.idInfo.region,
      catalogDisplayName: selectedPermission.catalogName,
      dataOwnerRole: isValidWorkspace(selectedPermission.owner)
        ? getFoundryRoleARN(props.idInfo.region, selectedPermission.catalogId)
        : selectedPermission.publisherRole,
      owner: selectedPermission.owner,
      itemType: RESOURCE_ITEM,
      permissionId: selectedPermission.permissionId,
      catalogName: selectedPermission.catalogName,
      id: selectedPermission.id,
      dataLakePrincipal: principal,
      clusterId: selectedPermission.clusterId,
      redshiftWorkgroupName: selectedPermission.redshiftWorkgroupName,
      bucketName: selectedPermission.bucketName,
      schemaName: selectedPermission.schemaName,
    };
  };

  useEffect(() => {
    let res = [];

    if (selectedPermission == undefined) {
      setResourceSummaryItems([]);
      return;
    }

    res.push({
      type: DATASET_RESOURCE_TYPE,
      resource: `${selectedPermission.catalogName}/${selectedPermission.databaseName}/${selectedPermission.datasetName}`,
      resourceLink: createDatasetDetailLink(generateIdFromIdInfo(props.idInfo)),
    });
    setResourceSummaryItems(res);
  }, [selectedPermission]);

  // Update the cart item whenever the principal is updated
  useEffect(() => {
    if (!selectedPermission) return;
    const ds = flattenDatasetAccess(selectedPermission, principal ?? props.activeWorkspace.accountId);
    props.setCartItemsToAdd([ds]);
  }, [principal]);

  const handleChange = (e) => {
    setSelectedPermission(e.detail.selectedOption);
    if (props.isDWaaSWorkspace) {
      if (props.idInfo?.clusterId != null) {
        let ds = flattenDatasetAccess(e.detail.selectedOption, props.activeWorkspace.accountId);
        props.setCartItemsToAdd([ds]);
      } else {
        props.setCartItemsToAdd(selectedRoles.map((role) => flattenDatasetAccess(e.detail.selectedOption, role)));
      }
      return;
    }
    let ds = flattenDatasetAccess(e.detail.selectedOption, principal ?? props.activeWorkspace.accountId);
    props.setCartItemsToAdd([ds]);
  };

  const handleDWaaSWorkspaceChange = (e) => {
    setSelectedGalaxiGroup(e.detail.selectedOption);
    setSelectedRoles([]);
    props.setCartItemsToAdd([]);
  };

  const handleRoleSelectionChange = (roles: string[]) => {
    setSelectedRoles(roles);
    if (!selectedPermission) return;
    const cartItems = roles.map((role) => flattenDatasetAccess(selectedPermission, role));
    props.setCartItemsToAdd(cartItems);
  };

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      <FormField
        label='Select permission'
        description={'Select resource access permission provided by the data provider'}
      >
        <Select
          selectedOption={selectedPermission}
          onChange={handleChange}
          options={props.options}
          selectedAriaLabel='Selected'
          empty='No access options'
          statusType={props.loading ? 'loading' : 'finished'}
          loadingText='Loading access options'
          data-testid={'select-permissions'}
        />
      </FormField>
      {selectedPermission && props.isDWaaSWorkspace && (
        <GalaxiGroupDropdown
          selectedOption={selectedGalaxiGroup}
          onChange={handleDWaaSWorkspaceChange}
          options={props.galaxiGroupOptions}
          errorText={undefined}
        />
      )}
      {selectedPermission && props.isDWaaSWorkspace && selectedGalaxiGroup && !props.idInfo?.clusterId && (
        <AccountAssociationCheckboxes
          accountAssociations={selectedGalaxiGroup['accountAssociations'] ?? []}
          selectedRoles={selectedRoles}
          onChange={handleRoleSelectionChange}
          errorText={selectedRoles.length === 0 ? 'Select at least one IAM role' : undefined}
        />
      )}
      {!!selectedPermission && !props.isDWaaSWorkspace && (
        <LfPrincipalTypeSelector
          disableAccountLevelAccess={false}
          loading={props.loading}
          principal={principal}
          setPrincipal={setPrincipal}
        />
      )}
      {resourceSummaryItems?.length > 0 &&
        (!props.isDWaaSWorkspace || selectedRoles.length > 0 || props.idInfo?.clusterId) && (
          <ResourceSummaryTable loading={props.loading} items={resourceSummaryItems} />
        )}
    </SpaceBetween>
  );
};
